import { Dispatch } from 'redux';
import { AxiosResponse } from 'axios';
import {
    DispatchFunction,
    IPolicyAuth,
    IActiveFilter,
    IRule,
    RuleElements,
    ConjuctionType,
    ActionTypes,
    ConditionTypes,
    CriteriaTypes,
    IConditionData,
    IActionData,
    IElseActionData,
    IAdditionalRule,
} from '@app/models';
import {
    GET_ALL_POLICIES_AUTH,
    GET_ALL_POLICIES_AUTH_LOADING,
    SET_AUTH_POLICIES_COUNT,
    ADD_RULE,
    REMOVE_RULE,
    RESET_RULES,
    ADD_RULE_ELEMENT,
    REMOVE_RULE_ELEMENT,
    UPDATE_RULE_ELEMENT,
    SET_RULE_OPERATOR,
    ADD_MAIN_RULE, ADD_MAIN_RULE_ELEMENT, SET_MAIN_RULE_OPERATOR, REMOVE_MAIN_RULE_ELEMENT, UPDATE_MAIN_RULE_ELEMENT
} from '@app/store/constants';
import { policiesAuthService } from '@app/services';
import { loadingData } from '@app/store/actions';
import { generateID } from '@app/helpers';

export function handleGetPoliciesAuthData(orderBy: string, asc: string, top: number, skip: number, isReset: boolean, filters?: IActiveFilter[]): DispatchFunction {
    return ((dispatch: Dispatch) => {
        if (isReset) {
            dispatch(loadingData(true));
            dispatch(setAuthPoliciesCount(0));
            dispatch(getAllPolicyAuths([]));
        }

        policiesAuthService.getPoliciesAuth(orderBy, asc, top, skip, isReset, filters)
            .then((response: AxiosResponse) => {
                dispatch(getAllPolicyAuths(response.data.value));

                if (isReset) {
                    dispatch(setAuthPoliciesCount(response.data['@odata.count']));
                    dispatch(loadingData(false));
                }
            })
            .catch(() => {
                dispatch(loadingData(false));
            });
    });
}

export function getAllPolicyAuths(policiesAuth: IPolicyAuth[]) {
    return {
        payload: policiesAuth,
        type: GET_ALL_POLICIES_AUTH
    };
}

export function getAllPolicyAuthsLoading(policiesAuthLoading: boolean) {
    return {
        payload: policiesAuthLoading,
        type: GET_ALL_POLICIES_AUTH_LOADING
    };
}

export function setAuthPoliciesCount(count: number) {
    return {
        payload: count,
        type: SET_AUTH_POLICIES_COUNT
    };
}

export function addMainRule(mainRule: IRule) {
    if (mainRule.id === undefined) {
        mainRule.id = generateID(20);
    }
    return {
        payload: mainRule,
        type: ADD_MAIN_RULE
    };
}

export function addRule(rule: IAdditionalRule) {
    if (rule.id === undefined) {
        rule.id = generateID(20);
    }
    return {
        payload: rule,
        type: ADD_RULE
    };
}

export function addMainRuleCondition(object: IConditionData | null) {
    const type: string = RuleElements.condition;
    const id = generateID(20);
    return {
        payload: { type, id, object },
        type: ADD_MAIN_RULE_ELEMENT
    };
}

export function addCondition(object: IConditionData | null, ruleID?: string) {
    const type: string = RuleElements.condition;
    const id = generateID(20);
    let obj = {
        payload: { ruleID, type, id, object },
        type: ADD_RULE_ELEMENT
    };

    if (!ruleID) {
        obj.type = ADD_MAIN_RULE_ELEMENT;
    }

    return obj;
}

export function addMainRuleAction(object: IActionData | null) {
    const type: string = RuleElements.action;
    const id = generateID(20);
    return {
        payload: { type, id, object },
        type: ADD_MAIN_RULE_ELEMENT
    };
}

export function addAction(object: IActionData | null, ruleID?: string) {
    const type: string = RuleElements.action;
    const id = generateID(20);
    let obj = {
        payload: { ruleID, type, id, object },
        type: ADD_RULE_ELEMENT
    };

    if (!ruleID) {
        obj.type = ADD_MAIN_RULE_ELEMENT;
    }

    return obj;
}

export function addMainRuleElseAction(object: IElseActionData | null) {
    const type: string = RuleElements.elseAction;
    const id = generateID(20);
    return {
        payload: { type, id, object },
        type: ADD_MAIN_RULE_ELEMENT
    };
}

export function addElseAction(ruleID: string, object: IElseActionData | null) {
    const type: string = RuleElements.elseAction;
    const id = generateID(20);
    return {
        payload: { ruleID, type, id, object },
        type: ADD_RULE_ELEMENT
    };
}

export function removeRule(ruleID: string) {
    return {
        payload: ruleID,
        type: REMOVE_RULE
    };
}

export function removeCondition(elementID: string, ruleID?: string) {
    const type: string = RuleElements.condition;
    let obj = {
        payload: { ruleID, type, elementID },
        type: REMOVE_RULE_ELEMENT
    };

    if (!ruleID) {
        obj.type = REMOVE_MAIN_RULE_ELEMENT;
    }

    return obj;
}

export function removeAction(elementID: string, ruleID?: string) {
    const type: string = RuleElements.action;
    let obj = {
        payload: { ruleID, type, elementID },
        type: REMOVE_RULE_ELEMENT
    };

    if (!ruleID) {
        obj.type = REMOVE_MAIN_RULE_ELEMENT;
    }

    return obj;
}

export function removeElseAction(elementID: string, ruleID?: string) {
    const type: string = RuleElements.elseAction;
    const obj = {
        payload: { ruleID, type, elementID },
        type: REMOVE_RULE_ELEMENT
    };

    if (!ruleID) {
        obj.type = REMOVE_MAIN_RULE_ELEMENT;
    }

    return obj;
}

export function updateCondition(elementID: string, data: any, conditionType: ConditionTypes, criteriaType: CriteriaTypes, ruleID?: string) {
    const type: string = RuleElements.condition;
    let obj = {
        payload: { ruleID, type, elementID, data, conditionType, criteriaType },
        type: UPDATE_RULE_ELEMENT
    };

    if (!ruleID) {
        obj.type = UPDATE_MAIN_RULE_ELEMENT;
    }

    return obj;
}

export function updateAction(elementID: string, data: any, actionType: ActionTypes, ruleID?: string) {
    const type: string = RuleElements.action;
    let obj = {
        payload: { ruleID, type, elementID, data, actionType },
        type: UPDATE_RULE_ELEMENT
    };

    if (!ruleID) {
        obj.type = UPDATE_MAIN_RULE_ELEMENT;
    }

    return obj;
}

export function updateElseAction(elementID: string, data: any, elseActionType: ActionTypes) {
    const type: string = RuleElements.elseAction;
    return {
        payload: { type, elementID, data, elseActionType },
        type: UPDATE_MAIN_RULE_ELEMENT
    };
}

export function setRuleOperator(operator: ConjuctionType, ruleID?: string) {
    let obj = {
        payload: { ruleID, operator },
        type: SET_RULE_OPERATOR
    };

    if (!ruleID) {
        obj.type = SET_MAIN_RULE_OPERATOR;
    }

    return obj;
}

export function resetRules() {
    return {
        type: RESET_RULES
    };
}
